import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal } from "@/components/Reveal";
import { API } from "@/assets";
import { useContact } from "@/components/useContent";
import { CheckCircle2, Loader2, Send, Mail, Phone, LifeBuoy } from "lucide-react";

const SERVICES = [
  "Music Production",
  "Mixing & Mastering",
  "Beat Licensing",
  "Artist Management",
  "Distribution",
  "Other / Not Sure Yet",
];
const BUDGETS = ["Under $500", "$500 - $1,500", "$1,500 - $5,000", "$5,000 - $15,000", "$15,000+"];

const inputCls =
  "w-full bg-obsidian/70 border border-white/12 rounded-xl px-4 py-3.5 text-sm text-ice placeholder:text-ice/35 focus:outline-none focus:border-venom/70 focus:ring-2 focus:ring-venom/20 transition-all duration-300";

export const QuoteSection = () => {
  const [params] = useSearchParams();
  const [form, setForm] = useState({ client_name: "", email: "", service: "", budget: "", details: "" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const contact = useContact();

  useEffect(() => {
    const service = params.get("service");
    const details = params.get("details");
    if (service && SERVICES.includes(service)) setForm((f) => ({ ...f, service }));
    if (details) setForm((f) => ({ ...f, details }));
  }, [params]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.service || !form.budget) {
      setError("Please select a service and a project budget.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/quotes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const detail = data.detail;
        throw new Error(
          typeof detail === "string"
            ? detail
            : Array.isArray(detail)
              ? detail.map((d) => d.msg).join(" ")
              : "Submission failed. Please try again."
        );
      }
      setSuccess(true);
      setForm({ client_name: "", email: "", service: "", budget: "", details: "" });
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="quote" className="relative py-20 lg:py-28" data-testid="quote-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 grid lg:grid-cols-[1fr_1.2fr] gap-12 lg:gap-20">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-venom font-medium mb-4">
            Work With Us
          </p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white leading-tight">
            Ready to make
            <br />
            <span className="text-stroke-crystal">a cold record?</span>
          </h2>
          <p className="mt-6 text-sm sm:text-base text-ice/70 leading-relaxed max-w-md">
            Tell us about the project. We review every request personally and hit you back within
            24 hours with a quote, a plan, and straight answers — no fluff.
          </p>
          <div className="mt-8 space-y-3">
            {["Response within 24 hours", "Transparent, itemized quotes", "Official ASCAP® & PRO Members"].map((line) => (
              <div key={line} className="flex items-center gap-3">
                <CheckCircle2 size={16} className="text-venom shrink-0" />
                <span className="text-sm text-ice/75">{line}</span>
              </div>
            ))}
          </div>

          <div className="mt-10 border border-crystal/25 rounded-2xl p-6 bg-charcoal" data-testid="contact-info-card">
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-ice/40 mb-4">Direct Lines</p>
            <a
              href={`mailto:${contact.email}`}
              className="flex items-center gap-3 text-sm text-ice/80 hover:text-venom transition-colors duration-300 break-all"
              data-testid="contact-email-link"
            >
              <Mail size={16} className="text-venom shrink-0" /> {contact.email}
            </a>
            <a
              href={`mailto:${contact.support}?subject=Support%20Request`}
              className="mt-2.5 flex items-center gap-3 text-sm text-ice/80 hover:text-venom transition-colors duration-300 break-all"
              data-testid="contact-support-link"
            >
              <LifeBuoy size={16} className="text-crystal shrink-0" /> {contact.support}
              <span className="font-mono text-[9px] uppercase tracking-[0.15em] text-ice/40 shrink-0">orders &amp; help</span>
            </a>
            <div className="mt-3 space-y-2">
              {contact.phones.map((p) => (
                <a
                  key={p}
                  href={`tel:${p.replace(/[^0-9]/g, "")}`}
                  className="flex items-center gap-3 text-sm text-ice/80 hover:text-venom transition-colors duration-300"
                  data-testid={`contact-phone-${p.replace(/[^0-9]/g, "")}`}
                >
                  <Phone size={16} className="text-crystal shrink-0" /> {p}
                </a>
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <form
            onSubmit={submit}
            className="bg-charcoal border border-crystal/25 rounded-2xl p-6 sm:p-9 shadow-[0_20px_80px_rgba(0,0,0,0.5)]"
            data-testid="quote-form"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="quote-name" className="block font-mono text-[11px] uppercase tracking-[0.2em] text-ice/60 mb-2">
                  Client Name *
                </label>
                <input
                  id="quote-name"
                  required
                  value={form.client_name}
                  onChange={set("client_name")}
                  placeholder="Your name or artist alias"
                  className={inputCls}
                  data-testid="quote-input-name"
                />
              </div>
              <div>
                <label htmlFor="quote-email" className="block font-mono text-[11px] uppercase tracking-[0.2em] text-ice/60 mb-2">
                  Email *
                </label>
                <input
                  id="quote-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={set("email")}
                  placeholder="you@example.com"
                  className={inputCls}
                  data-testid="quote-input-email"
                />
              </div>
              <div>
                <label htmlFor="quote-service" className="block font-mono text-[11px] uppercase tracking-[0.2em] text-ice/60 mb-2">
                  Service Needed *
                </label>
                <select
                  id="quote-service"
                  value={form.service}
                  onChange={set("service")}
                  className={`${inputCls} dge-select pr-10`}
                  data-testid="quote-select-service"
                >
                  <option value="" disabled>Select a service</option>
                  {SERVICES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="quote-budget" className="block font-mono text-[11px] uppercase tracking-[0.2em] text-ice/60 mb-2">
                  Project Budget *
                </label>
                <select
                  id="quote-budget"
                  value={form.budget}
                  onChange={set("budget")}
                  className={`${inputCls} dge-select pr-10`}
                  data-testid="quote-select-budget"
                >
                  <option value="" disabled>Select a range</option>
                  {BUDGETS.map((b) => (
                    <option key={b} value={b}>{b}</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="quote-details" className="block font-mono text-[11px] uppercase tracking-[0.2em] text-ice/60 mb-2">
                  Project Details / Notes
                </label>
                <textarea
                  id="quote-details"
                  rows={5}
                  value={form.details}
                  onChange={set("details")}
                  placeholder="Tell us about the record, the vibe, references, deadlines — anything that helps us quote it right."
                  className={`${inputCls} resize-none`}
                  data-testid="quote-input-details"
                />
              </div>
            </div>

            {error && (
              <p className="mt-4 text-sm text-red-400" role="alert" data-testid="quote-form-error">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="mt-7 w-full inline-flex items-center justify-center gap-2.5 bg-venom text-obsidian font-bold text-sm sm:text-base px-8 py-4 rounded-full hover:glow-venom hover:scale-[1.02] active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-300"
              data-testid="quote-form-submit-button"
            >
              {submitting ? <Loader2 size={18} className="animate-spin" /> : <Send size={17} />}
              {submitting ? "Sending…" : "Submit Quote Request"}
            </button>
          </form>
        </Reveal>
      </div>

      <AnimatePresence>
        {success && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-center justify-center bg-obsidian/80 backdrop-blur-md px-4"
            onClick={() => setSuccess(false)}
            data-testid="quote-success-modal"
          >
            <motion.div
              initial={{ scale: 0.85, y: 30, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.9, y: 20, opacity: 0 }}
              transition={{ type: "spring", damping: 22, stiffness: 260 }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-w-md w-full bg-charcoal border border-venom/40 rounded-2xl p-9 text-center glow-venom"
            >
              <div className="mx-auto w-16 h-16 rounded-full bg-venom/15 border border-venom/50 flex items-center justify-center mb-5">
                <CheckCircle2 size={30} className="text-venom" />
              </div>
              <h3 className="font-display text-xl font-bold text-white">Request received.</h3>
              <p className="mt-3 text-sm text-ice/70 leading-relaxed">
                Your quote request is in the lab. The DGE team will hit you back within 24 hours —
                keep an eye on your inbox.
              </p>
              <button
                onClick={() => setSuccess(false)}
                className="mt-7 bg-venom text-obsidian font-bold text-sm px-8 py-3 rounded-full hover:scale-[1.04] active:scale-95 transition-all duration-300"
                data-testid="quote-success-close-button"
              >
                Back to the Site
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
